import exifr from 'exifr';

export interface PhotoDetectionResult {
  date: string; // Formatted YYYY-MM-DD
  time?: string; // Formatted HH:mm
  dateTime?: Date;
  confidence: 'high' | 'medium' | 'fallback';
  source: 'exif' | 'filename' | 'fileModified';
  description: string;
  place?: string;
  matchedFileName?: string;
}

/**
 * Normalizes any date string or Date object into a valid Date and YYYY-MM-DD string.
 * Handles EXIF-specific format "YYYY:MM:DD HH:MM:SS" which standard JavaScript
 * `new Date()` fails to parse on Safari and many mobile browsers.
 */
export function normalizeDate(raw: unknown): { dateStr: string; timeStr?: string; dateObj: Date } | null {
  if (!raw) return null;

  let parsedDate: Date | null = null;

  if (raw instanceof Date) {
    if (!isNaN(raw.getTime())) {
      parsedDate = raw;
    }
  } else if (typeof raw === 'number') {
    // Timestamp (could be in seconds or ms)
    const ms = raw < 1e11 ? raw * 1000 : raw;
    const d = new Date(ms);
    if (!isNaN(d.getTime())) {
      parsedDate = d;
    }
  } else if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;

    // Convert EXIF "YYYY:MM:DD" to ISO standard "YYYY-MM-DD"
    // e.g. "2023:08:14 15:30:22" -> "2023-08-14T15:30:22"
    let isoCompatible = trimmed.replace(/^(\d{4})[:/](\d{2})[:/](\d{2})/, '$1-$2-$3');
    if (isoCompatible.includes(' ') && !isoCompatible.includes('T')) {
      isoCompatible = isoCompatible.replace(' ', 'T');
    }

    const d = new Date(isoCompatible);
    if (!isNaN(d.getTime())) {
      parsedDate = d;
    } else {
      // Direct regex extraction fallback
      const match = trimmed.match(/(\d{4})[-:/](\d{2})[-:/](\d{2})/);
      if (match) {
        const year = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        const day = parseInt(match[3], 10);
        const fallbackD = new Date(year, month, day);
        if (!isNaN(fallbackD.getTime())) {
          parsedDate = fallbackD;
        }
      }
    }
  }

  if (!parsedDate) return null;

  const year = parsedDate.getFullYear();
  // Filter out invalid or corruption years (e.g. 1970, 0000, 2099+)
  if (year < 1995 || year > 2040) return null;

  const yyyy = year.toString();
  const mm = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const dd = String(parsedDate.getDate()).padStart(2, '0');
  const dateStr = `${yyyy}-${mm}-${dd}`;

  const hh = String(parsedDate.getHours()).padStart(2, '0');
  const min = String(parsedDate.getMinutes()).padStart(2, '0');
  const timeStr = `${hh}:${min}`;

  return { dateStr, timeStr, dateObj: parsedDate };
}

/**
 * Attempts to extract the date from common photo file naming conventions.
 * WhatsApp, Android, Google Pixel, Samsung, Screenshots, Telegram, Signal, etc.
 */
export function extractDateFromFilename(fileName: string): { dateStr: string; timeStr?: string } | null {
  if (!fileName) return null;

  // 1. WhatsApp image or video: IMG-20231105-WA0004.jpg or VID-20231105-WA0004.mp4
  const waMatch = fileName.match(/(?:IMG|VID|WA)[-_](\d{4})(\d{2})(\d{2})[-_]/i);
  if (waMatch) {
    const y = parseInt(waMatch[1], 10);
    const m = parseInt(waMatch[2], 10);
    const d = parseInt(waMatch[3], 10);
    if (y >= 2000 && y <= 2035 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return { dateStr: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` };
    }
  }

  // 2. Android camera / Pixel / Samsung: IMG_20230815_142030.jpg or PXL_20240214_193022.jpg or 20240315_123456.jpg
  const cameraMatch = fileName.match(/(?:IMG|PXL|PHOTO|PIC|DSC|VID|MOV)?[-_]?(\d{4})(\d{2})(\d{2})[-_](\d{2})(\d{2})(\d{2})/i);
  if (cameraMatch) {
    const y = parseInt(cameraMatch[1], 10);
    const m = parseInt(cameraMatch[2], 10);
    const d = parseInt(cameraMatch[3], 10);
    const hh = cameraMatch[4];
    const mm = cameraMatch[5];
    if (y >= 2000 && y <= 2035 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return {
        dateStr: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
        timeStr: `${hh}:${mm}`,
      };
    }
  }

  // 3. Hyphenated ISO: Screenshot_2024-05-20-11-30.png or 2024-05-20_15.30.22
  const isoMatch = fileName.match(/(?:^|[^\d])(\d{4})[-_](\d{2})[-_](\d{2})(?:[^\d]|$)/);
  if (isoMatch) {
    const y = parseInt(isoMatch[1], 10);
    const m = parseInt(isoMatch[2], 10);
    const d = parseInt(isoMatch[3], 10);
    if (y >= 2000 && y <= 2035 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return { dateStr: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` };
    }
  }

  // 4. Day-Month-Year pattern: 15-08-2023 or 15_08_2023
  const dmyMatch = fileName.match(/(?:^|[^\d])(\d{2})[-_](\d{2})[-_](\d{4})(?:[^\d]|$)/);
  if (dmyMatch) {
    const d = parseInt(dmyMatch[1], 10);
    const m = parseInt(dmyMatch[2], 10);
    const y = parseInt(dmyMatch[3], 10);
    if (y >= 2000 && y <= 2035 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return { dateStr: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` };
    }
  }

  return null;
}

/**
 * Checks if a file is an image by MIME type or file extension.
 * Correctly accounts for iOS HEIC/HEIF files which browsers often label with empty type.
 */
export function isPhotoFile(file: File): boolean {
  if (file.type && file.type.startsWith('image/')) return true;
  const name = (file.name || '').toLowerCase();
  return /\.(jpg|jpeg|png|heic|heif|webp|avif|gif|bmp|tiff|tif|dng|raw)$/i.test(name);
}

/**
 * Inspects a single file and extracts EXIF, filename and modification date.
 */
export async function extractDateFromSingleFile(file: File): Promise<PhotoDetectionResult | null> {
  // Strategy 1: EXIF Metadata (Date extraction only - GPS strictly excluded)
  try {
    const exif = await exifr.parse(file, {
      tiff: true,
      xmp: true,
      iptc: true,
      mergeOutput: true,
      pick: [
        'DateTimeOriginal',
        'CreateDate',
        'DateTimeDigitized',
        'ModifyDate',
        'DateCreated',
        'GPSDateStamp',
      ],
    });

    if (exif) {
      const rawDate =
        exif.DateTimeOriginal ||
        exif.CreateDate ||
        exif.DateTimeDigitized ||
        exif.DateCreated ||
        exif.ModifyDate ||
        exif.GPSDateStamp;

      const normalized = normalizeDate(rawDate);
      if (normalized) {
        return {
          date: normalized.dateStr,
          time: normalized.timeStr,
          dateTime: normalized.dateObj,
          confidence: 'high',
          source: 'exif',
          description: 'Metadatos originales de la cámara (EXIF)',
          matchedFileName: file.name,
        };
      }
    }
  } catch {
    // Some formats or stripped files throw error in exifr, gracefully ignore
  }

  // Strategy 2: Filename pattern (e.g. WhatsApp, Android, Pixel, Samsung, Screenshots)
  const filenameResult = extractDateFromFilename(file.name);
  if (filenameResult) {
    return {
      date: filenameResult.dateStr,
      time: filenameResult.timeStr,
      confidence: 'medium',
      source: 'filename',
      description: 'Fecha registrada en el nombre de la foto',
      matchedFileName: file.name,
    };
  }

  // Strategy 3: File.lastModified (Fallback for files saved or downloaded in the past)
  if (file.lastModified) {
    const modDate = new Date(file.lastModified);
    if (!isNaN(modDate.getTime())) {
      const now = new Date();
      const isPastDate =
        modDate.getFullYear() !== now.getFullYear() ||
        modDate.getMonth() !== now.getMonth() ||
        modDate.getDate() !== now.getDate() ||
        now.getTime() - modDate.getTime() > 6 * 60 * 60 * 1000;

      if (isPastDate && modDate.getFullYear() >= 2000) {
        const normalized = normalizeDate(modDate);
        if (normalized) {
          return {
            date: normalized.dateStr,
            time: normalized.timeStr,
            dateTime: normalized.dateObj,
            confidence: 'fallback',
            source: 'fileModified',
            description: 'Fecha de creación del archivo en tu dispositivo',
            matchedFileName: file.name,
          };
        }
      }
    }
  }

  return null;
}

/**
 * Scans a batch of uploaded files and finds the most accurate capture date.
 * Prioritizes 'high' (EXIF), then 'medium' (filename), then 'fallback' (lastModified).
 */
export async function detectBestDateFromPhotoBatch(files: File[]): Promise<PhotoDetectionResult | null> {
  if (!files || files.length === 0) return null;

  const validFiles = files.filter((f) => isPhotoFile(f) || f.type.startsWith('video/'));
  if (validFiles.length === 0) return null;

  const results: PhotoDetectionResult[] = [];

  for (const file of validFiles) {
    const res = await extractDateFromSingleFile(file);
    if (res) {
      results.push(res);
      if (res.confidence === 'high') {
        return res;
      }
    }
  }

  if (results.length === 0) return null;

  // 1. First priority: High confidence (EXIF)
  const exifResults = results.filter((r) => r.confidence === 'high');
  if (exifResults.length > 0) {
    return exifResults[0];
  }

  // 2. Second priority: Medium confidence (Filename pattern)
  const filenameResults = results.filter((r) => r.confidence === 'medium');
  if (filenameResults.length > 0) {
    return filenameResults[0];
  }

  // 3. Third priority: Fallback (File modified)
  return results[0];
}

/**
 * Formats a YYYY-MM-DD date into a warm Spanish readable string:
 * e.g. "14 de Febrero de 2024"
 */
export function formatReadableSpanishDate(dateStr: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const year = parts[0];
  const monthIdx = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);

  if (monthIdx >= 0 && monthIdx < 12 && !isNaN(day)) {
    return `${day} de ${months[monthIdx]} de ${year}`;
  }

  return dateStr;
}
